import React, { useState, useCallback, useRef, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { useAppThunkDispatch } from "redux/store";
import { Avatar } from "component/shared/avatar";
import { FeatherIcon } from "component/shared/feather-icon";
import { Table } from "component/shared/table";
import { Agent } from "model/agent";
import { deleteAgent } from "redux/slice/agents";
import { updateTask } from "redux/slice/tasks";
import { numberedDateFormatter } from "shared/date-formatter";
import { AgentColumnType } from "type/agent-column-type";
import { DeleteAgentDialog } from "./delete-dialog";
import { styles } from "./style";
import { AddEditAgentDialog } from "../add-edit-agent-dialog";
import { urls } from "routes/urls";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { getGeographicRoutes, getLeadsOptimized, getRoundRobinRoutes, getTasks, getUser, getAgentTableColumns } from "redux/selector";
import { Select } from "component/shared/select";
import { updateAgentPageColumns } from "redux/slice/agent-page";
import { CopyID } from "component/shared/copy-id";
import moment from "moment";
import * as env from "shared/env";
import { useTheme } from "@mui/material/styles";

interface Props extends OwnProps, WithStyles<typeof styles> {}

interface OwnProps {
	agents: Agent[];
	maxForTeams?: boolean;
}

const AgentTableComponent: React.FC<Props> = ({ agents, maxForTeams, classes = {} }) => {
	const theme = useTheme();
	const dispatch = useAppThunkDispatch();
	
	// Redux selectors
	const columnTypesValues = useSelector(getAgentTableColumns);
	const user = useSelector(getUser);
	const tasks = useSelector(getTasks);
	const leads = useSelector(getLeadsOptimized);
	const roundRobinRoutes = useSelector(getRoundRobinRoutes);
	const geographicRoutingRoutes = useSelector(getGeographicRoutes);


	useEffect(() => {
		if (!columnTypesValues.length) {
			const colTypes = [
				AgentColumnType.AVATAR,
				AgentColumnType.LABEL,
				AgentColumnType.ID,
				AgentColumnType.ROLE,
				AgentColumnType.LAST_LOGIN,
				AgentColumnType.BILLABLE_USER,
				AgentColumnType.LEADS,
		// Team features columns - match deworsham's column visibility logic
		...(user.permissions.showTeamFeatures && user.permissions.roundRobinRouting ? [AgentColumnType.ROUND_ROBIN] : []),
		...(user.permissions.showTeamFeatures && user.permissions.geographicRouting ? [AgentColumnType.GEOGRAPHIC_ROUTING] : [])
			];

			dispatch(updateAgentPageColumns(colTypes));
		}
	}, [columnTypesValues.length, user.permissions.showTeamFeatures, user.permissions.roundRobinRouting, user.permissions.geographicRouting, dispatch])

	// Local state
	const [addAgentDialogIsOpen, setAddAgentDialogIsOpen] = useState(false);
	const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
	const [agentToEdit, setAgentToEdit] = useState<Agent | null>(null);
	const [deleteAgentDialogIsOpen, setDeleteAgentDialogIsOpen] = useState(false);

	const showAddAgentDialog = useCallback(() => {
		setAddAgentDialogIsOpen(true);
		setAgentToEdit(null);
	}, []);

	const showEditAgentDialog = useCallback((agent: Agent) => {
		setAddAgentDialogIsOpen(true);
		setAgentToEdit(agent);
	}, []);

	const hideAddAgentDialog = useCallback(() => {
		setAddAgentDialogIsOpen(false);
	}, []);

	const showDeleteAgentDialog = useCallback((agent: Agent) => {
		setAgentToDelete(agent);
		setDeleteAgentDialogIsOpen(true);
	}, []);

	const hideDeleteAgentDialog = useCallback(() => {
		setAgentToDelete(null);
		setDeleteAgentDialogIsOpen(false);
	}, []);

	const handleDeleteAgent = useCallback(() => {
		if (agentToDelete) {
			tasks.forEach(task => {
				if (task.agentId === agentToDelete.id) {
					dispatch(updateTask({ task: {
						...task,
						agentId: 0
					}}));
				}
			});
			dispatch(deleteAgent({ agent: {
				...agentToDelete,
			}}));
		}
		hideDeleteAgentDialog();
	}, [agentToDelete, tasks, dispatch, hideDeleteAgentDialog]);

	const columnOptions = useCallback(() => {
		const results: AgentColumnType[] = [
			AgentColumnType.AVATAR,
			AgentColumnType.LABEL,
			AgentColumnType.ID,
			AgentColumnType.ROLE,
			AgentColumnType.LAST_LOGIN,
			AgentColumnType.BILLABLE_USER,
			AgentColumnType.LEADS,
		// Team features columns - match deworsham's column visibility logic
		...(user.permissions.showTeamFeatures && user.permissions.roundRobinRouting ? [AgentColumnType.ROUND_ROBIN] : []),
		...(user.permissions.showTeamFeatures && user.permissions.geographicRouting ? [AgentColumnType.GEOGRAPHIC_ROUTING] : [])
		];
		return results;
	}, [user.permissions.showTeamFeatures, user.permissions.roundRobinRouting, user.permissions.geographicRouting]);


	const columns = [
							{
								id: "avatar",
								hidden: !columnTypesValues.includes(AgentColumnType.AVATAR),
								width: 70,
								value: (agent: Agent) => {
									return (
										<Mui.Link 
											className={classes.centeredCell}
											component={RouterLinkWrapper}
											to={urls.agentProfile(agent.id)}
										>
											<Avatar phrase={agent.label} src={agent.photoUrl} color={theme.palette.primary.main}/>
										</Mui.Link>
									);
								},
							},
							{
								id: "label",
								hidden: !columnTypesValues.includes(AgentColumnType.LABEL),
								title: AgentColumnType.LABEL.label,
								sort: (agent1: Agent, agent2: Agent) => {
									return (agent1.lastName || "").toLowerCase() < (agent2.lastName || "").toLowerCase()
										? -1
										: 1;
								},
								value: (agent: Agent) => {
									return (
										<Mui.Grid container direction="column">
											<Mui.Grid item>
												<Mui.Link
													className={classes.link}
													component={RouterLinkWrapper} 
													to={urls.agentProfile(agent.id)}
												>
													{agent.label}
												</Mui.Link>	
											</Mui.Grid>
											<Mui.Grid item>
												<Mui.Link 
													href={`mailto:${agent.email}`}
													className={classes.email}
												>
													{agent.email}
												</Mui.Link>
											</Mui.Grid>
										</Mui.Grid>
									);
								},
							},
							{
								id: AgentColumnType.ID.id,
								hidden: !columnTypesValues.includes(AgentColumnType.ID),
								title: AgentColumnType.ID.label,
								value: (agent: Agent) => {
									return (
										<CopyID id={agent.id} text="Copy Agent ID"/>
									);
								},
							},
							{
								id: AgentColumnType.ROLE.id,
								hidden: !columnTypesValues.includes(AgentColumnType.ROLE),
								title: AgentColumnType.ROLE.label,
								value: (agent: Agent) => {
									return (
										<Mui.Typography>
											{agent.admin ? "Admin" : ""}
										</Mui.Typography>
									);
								},
							},
							{
								id: AgentColumnType.BILLABLE_USER.id,
								hidden: !columnTypesValues.includes(AgentColumnType.BILLABLE_USER),
								title: (
									<div>
										{AgentColumnType.BILLABLE_USER.label}
										<br />
										<Mui.Typography style={{fontSize: 12}}>(billable user)</Mui.Typography>
									</div>
								),
								width: 180,
								value: (agent: Agent) => {
									if (agent.ownsLeads) {
										return (
											<Icons.Check/>
										)
									}
								}
							},
							{
								id: AgentColumnType.LEADS.id,
								hidden: !columnTypesValues.includes(AgentColumnType.LEADS),
								title: AgentColumnType.LEADS.label,
								value: (agent: Agent) => {
									const leadsByAgent = leads.filter(lead => lead.agent?.id === agent.id);
									if (leadsByAgent.length && agent.ownsLeads) {
										return (
											leadsByAgent.length
										)
									}
								}
							},
							{
								id: AgentColumnType.ROUND_ROBIN.id,
								hidden: !user.permissions.showTeamFeatures || !user.permissions.roundRobinRouting || !columnTypesValues.includes(AgentColumnType.ROUND_ROBIN),
								title: AgentColumnType.ROUND_ROBIN.label,
								value: (agent: Agent) => {
									const added = roundRobinRoutes.find(route => route.agent.id === agent.id);
									if (added) {
										return (
											<Icons.Check/>
										)
									}
								}
							},
							{
								id: AgentColumnType.GEOGRAPHIC_ROUTING.id,
								hidden: !user.permissions.showTeamFeatures || !user.permissions.geographicRouting || !columnTypesValues.includes(AgentColumnType.GEOGRAPHIC_ROUTING),
								width: 180,
								title: AgentColumnType.GEOGRAPHIC_ROUTING.label,
								value: (agent: Agent) => {
									const routes = geographicRoutingRoutes.filter(route => route.agent.id === agent.id);
									if (routes.length) {
										return (
											<>
												{routes.map((route, index) => (
													<Mui.Chip
														className={classes.chip}
														key={index}
														label={route.postalCode}
													/>
												))}
											</>
										)
									}
								}
							},
							{
								id: AgentColumnType.LAST_LOGIN.id,
								hidden: !columnTypesValues.includes(AgentColumnType.LAST_LOGIN),
								title: AgentColumnType.LAST_LOGIN.label,
								value: (agent: Agent) => {
									let lastLoginDate = agent.lastLoginDate;
									if (!lastLoginDate) {
										return null;
									}
									const date = moment(lastLoginDate);
									return numberedDateFormatter(date);
								},
							},
							{
								id: AgentColumnType.ACTION.id,
								title: AgentColumnType.ACTION.label,
								width: 180,
								value: (agent: Agent) => {
									const url = agent.hasOwnSite
									? (agent.redirectUrl || agent.agentPageUrl)
									: agent.agentPageUrl;
									return url ? (
										<Mui.Grid container alignItems="center" direction="row" spacing={5}>
											{/*
											//TODO: Handle login
											<Mui.Grid item>
												<Mui.Typography align="center">-------</Mui.Typography>
											</Mui.Grid>*/}
											<Mui.Grid item>
												<Mui.Link href={url} target="_blank" rel="noopener">
													View Profile Page
												</Mui.Link>
											</Mui.Grid>
										</Mui.Grid>
									) : null;
								},
							},
							{
								id: "delete",
								width: 70,
								showOnHover: true,
								value: (agent: Agent) => {
									return (
										<>
											{user.admin && (
												<Mui.IconButton
													onClick={() => {
														showDeleteAgentDialog(agent);
													}}
													disabled={(user.agentId === agent.id)}
												>
													<FeatherIcon>
														<Icons.Trash />
													</FeatherIcon>
												</Mui.IconButton>
											)}
										</>
									);
								},
							},
						]

	return (
		<>
			{addAgentDialogIsOpen && (
				<AddEditAgentDialog
					agents={agents}
					open={addAgentDialogIsOpen}
					onClose={hideAddAgentDialog}
				/>
			)}
			{agentToDelete && (
				<DeleteAgentDialog
					agent={agentToDelete}
					deleteAgent={handleDeleteAgent}
					deleteAgentDialogIsOpen={deleteAgentDialogIsOpen}
					hideDeleteAgentDialog={hideDeleteAgentDialog}
				/>
			)}
			<Mui.Grid container direction="column" spacing={4}>
				<Mui.Grid item>
					<Mui.Grid
						container
						item
						alignItems="center"
						justifyContent="space-between"
						spacing={4}
					>
						<Mui.Grid item>
							<Mui.Button
								variant="contained"
								color={"primary"}
								onClick={showAddAgentDialog}
							>
								<Mui.Typography>Add New</Mui.Typography>
							</Mui.Button>
						</Mui.Grid>
						{user.permissions.showTeamFeatures && (
							<Mui.Grid item>
							<Select
								multiple
								placeholder="Displayed Columns"
								value={columnTypesValues}
								options={columnOptions()}
								valueExtractor={(type) => type}
								labelExtractor={(type) => type.label}
								numberVisible={7}
								optionRenderer={(type) => (
									<Mui.ListItemText primary={type.label} />
								)}
								onChange={(values) => {
									const types = values as AgentColumnType[];
									dispatch(updateAgentPageColumns(types));
								}}
							/>
							</Mui.Grid>
						)}
					</Mui.Grid>
				</Mui.Grid>
				{/* MAX-4128: agents.length, not showTeamFeatures — that flag is agentCount > 1, so it hid the solo admin. */}
				{agents.length > 0 ? (
					<Mui.Grid item>
						<p>Total Billable Users: {agents.filter(agent => agent.ownsLeads).length}</p>
						<Table
							items={agents}
							columns={columns}
						/>
					</Mui.Grid>
				) : (
					<Mui.Grid item>
						<Mui.Grid container direction="column" alignItems="center" spacing={3} style={{padding: '48px 0'}}>
							<Mui.Grid item>
								<Mui.Typography variant="h5" align="center">
									No team members yet
								</Mui.Typography>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Typography variant="body1" align="center" color="textSecondary">
									Add your first team member to get started
								</Mui.Typography>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
				)}
			</Mui.Grid>
		</>
	);
};

export const AgentTable = withStyles(AgentTableComponent, styles);
